import React from "react";
import {
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import RealgemsSidebar from "./RealgemsSidebar";
import Topbar from "../menu/Topbar";
import Api from "../Services/Api";
import { useAuth } from "../auth/AuthContext";
import { isRealgemsAdmin, normalizeAccessType } from "../../utils/accessControl";
import {
  EMPTY_FORM,
  formatCnpj,
  formatPhone,
  formatZipCode,
  getApprovalStatusMeta,
  mapClientToForm,
  normalizeSearchText,
  shortenText,
  sortByUpdatedAtDesc,
} from "./realgemsClientHelpers";
import "./RealgemsClients.css";

const PAGE_SIZE = 50;

const tableCellSx = {
  borderBottom: "1px solid #eef2f7",
  color: "#334155",
  fontSize: "0.88rem",
  py: 1.55,
  whiteSpace: "nowrap",
};

const headCellSx = {
  borderBottom: "1px solid #e2e8f0",
  color: "#64748b",
  fontSize: "0.78rem",
  fontWeight: 800,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  backgroundColor: "#f8fafc",
};

const RealgemsClients = () => {
  const { user } = useAuth();
  const [menuClose, setMenuClose] = React.useState(false);
  const [clients, setClients] = React.useState([]);
  const [form, setForm] = React.useState(EMPTY_FORM);
  const [loading, setLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState("");
  const [success, setSuccess] = React.useState("");
  const [isCreateOpen, setIsCreateOpen] = React.useState(false);
  const [editingId, setEditingId] = React.useState(null);
  const [viewingClient, setViewingClient] = React.useState(null);
  const [search, setSearch] = React.useState("");
  const [currentPage, setCurrentPage] = React.useState(1);
  const [zipCodeLoading, setZipCodeLoading] = React.useState(false);
  const [zipCodeError, setZipCodeError] = React.useState("");

  const toggleMenu = () => setMenuClose((current) => !current);
  const isEditing = Boolean(editingId);
  const isAdminViewer = isRealgemsAdmin(normalizeAccessType(user?.access_type));

  const loadClients = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await Api.get("/realgems/clients");
      setClients(sortByUpdatedAtDesc(Array.isArray(response?.data?.items) ? response.data.items : []));
    } catch (err) {
      console.error("Erro ao carregar clientes Realgems:", err);
      setError(err?.response?.data?.message || "Não foi possível carregar os clientes.");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadClients();
  }, [loadClients]);

  React.useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  React.useEffect(() => {
    const zipCode = String(form.zipCode || "").replace(/\D/g, "");

    if (zipCode.length !== 8) {
      setZipCodeLoading(false);
      setZipCodeError("");
      return undefined;
    }

    const controller = new AbortController();

    const findAddress = async () => {
      try {
        setZipCodeLoading(true);
        setZipCodeError("");
        const response = await fetch(`https://viacep.com.br/ws/${zipCode}/json/`, {
          signal: controller.signal,
        });

        if (!response.ok) throw new Error("Falha ao consultar o CEP.");
        const address = await response.json();
        if (address.erro) throw new Error("CEP não encontrado.");

        setForm((current) => {
          if (String(current.zipCode || "").replace(/\D/g, "") !== zipCode) return current;

          const street = address.logradouro || "";
          const neighborhood = address.bairro || "";
          const city = address.localidade || "";
          const state = address.uf || "";
          const fullAddress = [
            [street, current.addressNumber].filter(Boolean).join(", "),
            neighborhood,
          ].filter(Boolean).join(" - ");

          return {
            ...current,
            street,
            neighborhood,
            city,
            state,
            fullAddress,
            cityState: [city, state].filter(Boolean).join("/"),
          };
        });
      } catch (err) {
        if (err.name === "AbortError") return;
        setZipCodeError(err.message || "Não foi possível consultar o CEP.");
      } finally {
        if (!controller.signal.aborted) setZipCodeLoading(false);
      }
    };

    findAddress();
    return () => controller.abort();
  }, [form.zipCode]);

  React.useEffect(() => {
    if (!isEditing) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setEditingId(null);
        setViewingClient(null);
        setForm(EMPTY_FORM);
        setError("");
        setSuccess("");
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isEditing]);

  const resetMessages = () => {
    setError("");
    setSuccess("");
    setZipCodeError("");
  };

  const closeCreatePanel = () => {
    setIsCreateOpen(false);
    setForm(EMPTY_FORM);
    resetMessages();
  };

  const closeEditDrawer = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    resetMessages();
  };

  const closeViewModal = () => {
    setViewingClient(null);
  };

  const openCreatePanel = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setIsCreateOpen(true);
    resetMessages();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleChange = (field) => (event) => {
    const rawValue = event.target.value;

    setForm((current) => {
      if (field === "cnpj") return { ...current, cnpj: formatCnpj(rawValue) };
      if (field === "zipCode") {
        return {
          ...current,
          zipCode: formatZipCode(rawValue),
          street: "",
          neighborhood: "",
          city: "",
          state: "",
          addressNumber: "",
          fullAddress: "",
          cityState: "",
        };
      }
      if (field === "addressNumber") {
        const fullAddress = [
          [current.street, rawValue].filter(Boolean).join(", "),
          current.neighborhood,
        ].filter(Boolean).join(" - ");
        return { ...current, addressNumber: rawValue, fullAddress };
      }
      if (field === "phone") return { ...current, phone: formatPhone(rawValue) };

      return {
        ...current,
        [field]: rawValue,
      };
    });

    resetMessages();
  };

  const handleEdit = (client) => {
    if (client.approvalStatus !== "REJECTED") return;
    setIsCreateOpen(false);
    setViewingClient(null);
    setEditingId(client.id);
    setForm(mapClientToForm(client));
    resetMessages();
  };

  const filteredClients = clients.filter((client) => {
    const query = normalizeSearchText(search);
    if (!query) return true;

    const searchableText = normalizeSearchText(
      [
        client.tradeName,
        client.companyLegalName,
        client.ownerUserName,
        client.cnpj,
        client.businessSegment,
        client.primaryCnae,
        client.cityState,
        client.phone,
        client.corporateEmail,
        client.website,
        client.clientNotes,
        getApprovalStatusMeta(client.approvalStatus).label,
        client.approvalNotes,
      ].join(" ")
    );

    return searchableText.includes(query);
  });

  const totalPages = Math.max(1, Math.ceil(filteredClients.length / PAGE_SIZE));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const paginatedClients = filteredClients.slice(
    (safeCurrentPage - 1) * PAGE_SIZE,
    safeCurrentPage * PAGE_SIZE
  );

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      setSubmitting(true);
      resetMessages();

      const payload = { ...form };
      const response = isEditing
        ? await Api.put(`/realgems/clients/${editingId}`, payload)
        : await Api.post("/realgems/clients", payload);

      const savedItem = response?.data?.item;
      if (savedItem) {
        setClients((current) =>
          sortByUpdatedAtDesc(
            isEditing
              ? current.map((item) => (item.id === editingId ? savedItem : item))
              : [savedItem, ...current]
          )
        );
      } else {
        await loadClients();
      }

      if (isEditing) {
        setSuccess(response?.data?.message || "Cadastro corrigido e reenviado para análise.");
        window.setTimeout(() => {
          closeEditDrawer();
        }, 450);
      } else {
        setSuccess("Cliente cadastrado com sucesso.");
        setForm(EMPTY_FORM);
        setIsCreateOpen(false);
      }
    } catch (err) {
      console.error("Erro ao salvar cliente Realgems:", err);
      setError(err?.response?.data?.message || "Não foi possível salvar o cliente.");
    } finally {
      setSubmitting(false);
    }
  };

  const renderFormFields = () => (
    <>
      <label className="realgems-clients-field">
        <span>Razão Social</span>
        <input value={form.companyLegalName} onChange={handleChange("companyLegalName")} />
      </label>

      <label className="realgems-clients-field">
        <span>Nome Fantasia</span>
        <input value={form.tradeName} onChange={handleChange("tradeName")} />
      </label>

      <label className="realgems-clients-field">
        <span>CNPJ</span>
        <input
          value={form.cnpj}
          onChange={handleChange("cnpj")}
          placeholder="00.000.000/0000-00"
          inputMode="numeric"
        />
      </label>

      <label className="realgems-clients-field">
        <span>Data de Fundação</span>
        <input type="date" value={form.foundationDate} onChange={handleChange("foundationDate")} />
      </label>

      <label className="realgems-clients-field">
        <span>Inscrição Estadual</span>
        <input value={form.stateRegistration} onChange={handleChange("stateRegistration")} />
      </label>

      <label className="realgems-clients-field">
        <span>Segmento de Atuação</span>
        <input value={form.businessSegment} onChange={handleChange("businessSegment")} />
      </label>

      <label className="realgems-clients-field">
        <span>CNAE Principal</span>
        <input value={form.primaryCnae} onChange={handleChange("primaryCnae")} />
      </label>

      <label className="realgems-clients-field">
        <span>CEP</span>
        <input
          value={form.zipCode}
          onChange={handleChange("zipCode")}
          placeholder="00000-000"
          inputMode="numeric"
          maxLength={9}
          required
        />
        {zipCodeLoading ? <small className="realgems-clients-field-hint">Buscando endereço...</small> : null}
        {zipCodeError ? <small className="realgems-clients-field-error">{zipCodeError}</small> : null}
      </label>

      <label className="realgems-clients-field">
        <span>Número</span>
        <input
          value={form.addressNumber}
          onChange={handleChange("addressNumber")}
          placeholder="Número do imóvel"
          disabled={!form.city || zipCodeLoading}
          required
        />
      </label>

      <label className="realgems-clients-field realgems-clients-field-wide">
        <span>Logradouro</span>
        <input value={form.street} readOnly placeholder="Preenchido automaticamente pelo CEP" />
      </label>

      <label className="realgems-clients-field">
        <span>Bairro</span>
        <input value={form.neighborhood} readOnly placeholder="Preenchido automaticamente" />
      </label>

      <label className="realgems-clients-field">
        <span>Cidade</span>
        <input value={form.city} readOnly placeholder="Preenchida automaticamente" />
      </label>

      <label className="realgems-clients-field">
        <span>Estado</span>
        <input value={form.state} readOnly placeholder="UF" />
      </label>

      <label className="realgems-clients-field">
        <span>Telefone</span>
        <input
          value={form.phone}
          onChange={handleChange("phone")}
          placeholder="(11) 99999-9999"
          inputMode="tel"
        />
      </label>

      <label className="realgems-clients-field">
        <span>E-mail Corporativo</span>
        <input
          type="email"
          value={form.corporateEmail}
          onChange={handleChange("corporateEmail")}
        />
      </label>

      <label className="realgems-clients-field">
        <span>Site</span>
        <input value={form.website} onChange={handleChange("website")} />
      </label>

      <label className="realgems-clients-field">
        <span>Instagram / Redes Sociais</span>
        <input value={form.socialMedia} onChange={handleChange("socialMedia")} />
      </label>

      <label className="realgems-clients-field realgems-clients-field-wide">
        <span>Observação</span>
        <textarea
          value={form.clientNotes}
          onChange={handleChange("clientNotes")}
          placeholder="Preencha se houver alguma observação importante sobre o cliente."
          rows={4}
        />
      </label>
    </>
  );

  return (
    <div className="app-container realgems-clients-page">
      <RealgemsSidebar close={menuClose} />

      <main className="main-content realgems-clients-main-content">
        <Topbar page="Clientes Realgems" onToggleMenu={toggleMenu} />

        <section className="realgems-clients-stack">
          <article className="realgems-clients-panel realgems-clients-create-panel">
            <div className="realgems-clients-panel-header">
              <button
                type="button"
                className="realgems-clients-primary-btn"
                onClick={() => (isCreateOpen ? closeCreatePanel() : openCreatePanel())}
              >
                {isCreateOpen ? "Fechar cadastro" : "Novo cadastro"}
              </button>
            </div>

            {isCreateOpen ? (
              <form className="realgems-clients-form realgems-clients-create-form" onSubmit={handleSubmit}>
                {renderFormFields()}

                <div className="realgems-clients-actions">
                  <button type="submit" disabled={submitting || zipCodeLoading || Boolean(zipCodeError)}>
                    {submitting ? "Cadastrando..." : "Cadastrar cliente"}
                  </button>
                </div>

                {error ? <div className="realgems-clients-message error">{error}</div> : null}
                {success ? <div className="realgems-clients-message success">{success}</div> : null}
              </form>
            ) : (
              <div className="realgems-clients-collapsed">
                <div className="realgems-clients-collapsed-copy" />
              </div>
            )}
          </article>

          <article className="realgems-clients-panel">
            <div className="realgems-clients-panel-header compact">
              <div>
                <span className="realgems-clients-kicker">Lista</span>
                <h2>Clientes cadastrados</h2>
                <p>
                  {loading
                    ? "Carregando..."
                    : isAdminViewer
                    ? `${filteredClients.length} cliente(s) encontrado(s) de ${clients.length} cadastrado(s) no portal Realgems.`
                    : `${filteredClients.length} cliente(s) encontrado(s) de ${clients.length} vinculado(s) ao seu usuário.`}
                </p>
              </div>
            </div>

            <div className="realgems-clients-toolbar">
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Buscar por nome, CNPJ, cidade, e-mail ou telefone"
                className="realgems-clients-search"
              />
            </div>

            {loading ? (
              <div className="realgems-clients-empty-state">Carregando clientes...</div>
            ) : filteredClients.length === 0 ? (
              <div className="realgems-clients-empty-state">
                {clients.length === 0 ? (
                  <>
                    Nenhum cliente cadastrado ainda. Clique em <strong>Novo cadastro</strong> para adicionar o
                    primeiro.
                  </>
                ) : (
                  <>Nenhum cliente encontrado com esse filtro.</>
                )}
              </div>
            ) : (
              <>
                <TableContainer component={Paper} className="realgems-clients-table-wrap">
                  <Table sx={{ minWidth: isAdminViewer ? 980 : 860 }} aria-label="Tabela de clientes Realgems">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={headCellSx}>Nome Fantasia</TableCell>
                        <TableCell sx={headCellSx}>CNPJ</TableCell>
                        {isAdminViewer ? <TableCell sx={headCellSx}>Cadastrado por</TableCell> : null}
                        <TableCell sx={headCellSx}>Status</TableCell>
                        <TableCell sx={headCellSx}>Observação</TableCell>
                        <TableCell sx={{ ...headCellSx, textAlign: "right" }}>Ações</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {paginatedClients.map((client) => {
                        const statusMeta = getApprovalStatusMeta(client.approvalStatus);

                        return (
                          <TableRow key={client.id} hover>
                            <TableCell sx={tableCellSx}>
                              <strong className="realgems-clients-table-strong">{client.tradeName}</strong>
                            </TableCell>
                            <TableCell sx={tableCellSx}>{formatCnpj(client.cnpj)}</TableCell>
                            {isAdminViewer ? (
                              <TableCell sx={tableCellSx}>{client.ownerUserName || "-"}</TableCell>
                            ) : null}
                            <TableCell sx={tableCellSx}>
                              <Chip
                                label={statusMeta.label}
                                size="small"
                                className={`realgems-client-status-badge ${statusMeta.className}`}
                              />
                            </TableCell>
                            <TableCell sx={{ ...tableCellSx, whiteSpace: "normal", minWidth: 240 }}>
                              {client.approvalStatus === "REJECTED" && client.approvalNotes
                                ? shortenText(client.approvalNotes, 120)
                                : "-"}
                            </TableCell>
                            <TableCell sx={tableCellSx} align="right">
                              <div className="realgems-clients-table-actions">
                                <button
                                  type="button"
                                  className="realgems-client-view-btn"
                                  onClick={() => setViewingClient(client)}
                                >
                                  Ver cliente
                                </button>

                                {client.approvalStatus === "REJECTED" ? (
                                  <button
                                    type="button"
                                    className="realgems-client-edit-btn"
                                    onClick={() => handleEdit(client)}
                                  >
                                    Corrigir cadastro
                                  </button>
                                ) : null}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {totalPages > 1 ? (
                  <div className="realgems-clients-pagination">
                    <button
                      type="button"
                      onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                      disabled={safeCurrentPage === 1}
                    >
                      Anterior
                    </button>

                    <span>
                      Página {safeCurrentPage} de {totalPages}
                    </span>

                    <button
                      type="button"
                      onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                      disabled={safeCurrentPage === totalPages}
                    >
                      Próxima
                    </button>
                  </div>
                ) : null}
              </>
            )}
          </article>
        </section>

        <div
          className={`realgems-clients-drawer-backdrop ${isEditing ? "is-open" : ""}`}
          onClick={closeEditDrawer}
          aria-hidden={!isEditing}
        />

        <aside className={`realgems-clients-drawer ${isEditing ? "is-open" : ""}`} aria-hidden={!isEditing}>
          <div className="realgems-clients-drawer-header">
            <div>
              <span className="realgems-clients-kicker">Edição</span>
              <h2>Editar cliente</h2>
            </div>

            <button type="button" className="realgems-clients-drawer-close" onClick={closeEditDrawer}>
              Fechar
            </button>
          </div>

          <form className="realgems-clients-form realgems-clients-drawer-form" onSubmit={handleSubmit}>
            {renderFormFields()}

            <div className="realgems-clients-actions">
              <button type="submit" disabled={submitting || zipCodeLoading || Boolean(zipCodeError)}>
                {submitting ? "Salvando alterações..." : "Salvar alterações"}
              </button>
            </div>

            {error ? <div className="realgems-clients-message error">{error}</div> : null}
            {success ? <div className="realgems-clients-message success">{success}</div> : null}
          </form>
        </aside>

        <div
          className={`realgems-clients-modal-backdrop ${viewingClient ? "is-open" : ""}`}
          onClick={closeViewModal}
          aria-hidden={!viewingClient}
        />

        <section className={`realgems-clients-modal ${viewingClient ? "is-open" : ""}`} aria-hidden={!viewingClient}>
          <div className="realgems-clients-modal-card">
            <div className="realgems-clients-modal-header">
              <div>
                <span className="realgems-clients-kicker">Cliente</span>
                <h2>Visualizar cliente</h2>
              </div>

              <button type="button" className="realgems-clients-drawer-close" onClick={closeViewModal}>
                Fechar
              </button>
            </div>

            {viewingClient ? (
              <div className="realgems-clients-view-grid">
                <div><strong>Status:</strong> {getApprovalStatusMeta(viewingClient.approvalStatus).label}</div>
                <div><strong>Nome Fantasia:</strong> {viewingClient.tradeName}</div>
                <div><strong>Razão Social:</strong> {viewingClient.companyLegalName}</div>
                <div><strong>CNPJ:</strong> {formatCnpj(viewingClient.cnpj)}</div>
                <div><strong>Data de Fundação:</strong> {viewingClient.foundationDate || "-"}</div>
                <div><strong>Inscrição Estadual:</strong> {viewingClient.stateRegistration}</div>
                <div><strong>Segmento de Atuação:</strong> {viewingClient.businessSegment}</div>
                <div><strong>CNAE Principal:</strong> {viewingClient.primaryCnae}</div>
                <div><strong>Endereço Completo:</strong> {viewingClient.fullAddress}</div>
                <div><strong>Cidade/UF:</strong> {viewingClient.cityState}</div>
                <div><strong>CEP:</strong> {viewingClient.zipCode}</div>
                <div><strong>Telefone:</strong> {viewingClient.phone}</div>
                <div><strong>E-mail Corporativo:</strong> {viewingClient.corporateEmail}</div>
                <div><strong>Site:</strong> {viewingClient.website}</div>
                <div><strong>Instagram / Redes Sociais:</strong> {viewingClient.socialMedia}</div>
                {isAdminViewer && viewingClient.ownerUserName ? (
                  <div><strong>Cadastrado por:</strong> {viewingClient.ownerUserName}</div>
                ) : null}
                <div className="realgems-clients-view-grid-wide">
                  <strong>Observação do usuário:</strong> {viewingClient.clientNotes || "-"}
                </div>
                <div className="realgems-clients-view-grid-wide">
                  <strong>Observação da aprovação:</strong> {viewingClient.approvalNotes || "-"}
                </div>
              </div>
            ) : null}
          </div>
        </section>
      </main>
    </div>
  );
};

export default RealgemsClients;
